import { formatDisplayTime, type TimePickerValue } from '@repo/features/time-picker'
import type { GetLoadDuplicateTemplate } from '@repo/schema/load-duplicate'
import { formatCurrency, formatPlaceSummary, toMinorUnits } from '@repo/sdk/helpers'

import {
  calendarDayNumber,
  fromUtcDay,
} from '@/app/@protected/_components/load-quick-create/_utils/calendar-date.ts'
import {
  getTimeLabel,
  type StopFieldValues,
} from '@/app/@protected/_components/load-quick-create/quick-create-stop-field.tsx'

/** The card's errors, by the path of the field each belongs to. */
export type DuplicateCardErrors = Partial<
  Record<'customer_rate_amount' | `drops[${number}].date`, string>
>

/** What a Duplicate card edits: each Stop's date, time and reference, and the customer rate. */
export type DuplicateCardValues = {
  /** In major units; empty saves as 0. */
  customer_rate_amount: string
  customer_rate_currency: string
  drops: StopFieldValues[]
}

export type DuplicateDateRules = {
  /** `loads:update-drop-past-dates`: the user may pick a date before today. */
  canSetPastDates: boolean
  today: Date
}

/**
 * What a duplication session starts from, read once as it opens: the template, and the Original
 * Load's own details of each template Stop.
 */
export type DuplicateOrigin = {
  /** One per template Stop. */
  originalStops: OriginalStop[]
  template: GetLoadDuplicateTemplate
}

/** What a Duplicate starts from, and which of the Original Load's dates and rate it keeps. */
export type DuplicateStart = DuplicateOrigin & {
  /** "Keep dates and times from the original load". */
  keepDates: boolean
  /** "Use original customer rate". */
  useOriginalRate: boolean
}

/**
 * One of the Original Load's Stops: what the template resets that a Duplicate may keep, and where
 * it is, as in "GB, N14 5JN, London".
 */
export type OriginalStop = { date: Date | undefined; place: string; reference_no: null | string }

/** A Stop of the Original Load, as its details read it. */
type OriginalLoadStop = {
  date?: Date | null
  order: number
  place?: Parameters<typeof formatPlaceSummary>[0]
  reference_no?: null | string
}

const DEFAULT_RATE_CURRENCY = 'GBP'

/**
 * The Original Load's Stop dates, references and places, paired with the template's Stops by route
 * position. Stops can share a position, so the nth template Stop at a position pairs with the nth
 * of the Original Load's Stops there, each in Route order. A Stop whose place has no country,
 * postcode or city keeps its saved name.
 */
export const getOriginalStops = (
  template: GetLoadDuplicateTemplate,
  drops: OriginalLoadStop[],
): OriginalStop[] =>
  template.drops.map((stop, index) => {
    const sharing = template.drops.slice(0, index).filter(({ order }) => order === stop.order)
    const original = drops.filter(({ order }) => order === stop.order)[sharing.length]
    return {
      date: original?.date ? fromUtcDay(original.date) : undefined,
      place: formatPlaceSummary(original?.place) || (stop.name ?? ''),
      reference_no: original?.reference_no ?? null,
    }
  })

/** The Original Load's customer rate in major units, as the card edits it; empty without one. */
export const getOriginalRateAmount = ({ customer_rate }: GetLoadDuplicateTemplate) =>
  customer_rate ? String(customer_rate.amount / 100) : ''

/** The card with the Original Load's Stop dates when they are kept, and no dates otherwise. */
export const withStartDates = (
  card: DuplicateCardValues,
  { keepDates, originalStops }: DuplicateStart,
): DuplicateCardValues => ({
  ...card,
  drops: card.drops.map((stop, index) => ({
    ...stop,
    date: keepDates ? originalStops[index]?.date : undefined,
  })),
})

/**
 * The card with the Original Load's customer rate, amount and currency, when it is used, and an
 * empty amount otherwise.
 */
export const withStartRate = (
  card: DuplicateCardValues,
  { template, useOriginalRate }: DuplicateStart,
): DuplicateCardValues =>
  useOriginalRate && template.customer_rate
    ? {
        ...card,
        customer_rate_amount: getOriginalRateAmount(template),
        customer_rate_currency: template.customer_rate.currency,
      }
    : { ...card, customer_rate_amount: '' }

/**
 * A new card: the Original Load's Stops and time windows, the first Stop's reference empty and the
 * other Stops' kept, and the dates and rate `start` keeps.
 */
export const getDuplicateCardDefaults = (start: DuplicateStart): DuplicateCardValues => {
  const { originalStops, template } = start
  const card: DuplicateCardValues = {
    customer_rate_amount: '',
    customer_rate_currency: template.customer_rate?.currency ?? DEFAULT_RATE_CURRENCY,
    drops: template.drops.map((stop, index) => ({
      date: undefined,
      id: `duplicate-stop-${index}`,
      place: { location_id: stop.location_id, name: stop.name },
      // Only the collection Stop's reference is new; the reference field is the first Stop's.
      reference_no: index === 0 ? null : (originalStops[index]?.reference_no ?? null),
      // A Stop saved without a time window shows as "TBC".
      time: stop.time ?? { type: 'tbc' },
      type: stop.type,
    })),
  }
  return withStartRate(withStartDates(card, start), start)
}

/**
 * The card after "Create another duplicate": the same Stop dates, times and customer rate, and an
 * empty first Stop reference, because each belongs to one booking.
 */
export const getNextDuplicateCard = (card: DuplicateCardValues): DuplicateCardValues => ({
  ...card,
  drops: card.drops.map((stop, index) => (index === 0 ? { ...stop, reference_no: null } : stop)),
})

/**
 * The card's errors by field path. Only the first Stop needs a date; dates follow Route order; a
 * date before `today` (a local calendar day) needs `canSetPastDates`; the rate may be empty.
 */
export const getDuplicateCardErrors = (
  values: DuplicateCardValues,
  { canSetPastDates, today }: DuplicateDateRules,
) => {
  const errors: DuplicateCardErrors = {}
  let previousDay: number | undefined

  values.drops.forEach((stop, index) => {
    const path = `drops[${index}].date` as const
    if (!stop.date) {
      if (index === 0) errors[path] = 'Select the date of the first Stop.'
      return
    }

    const day = calendarDayNumber(stop.date)
    if (!canSetPastDates && day < calendarDayNumber(today)) {
      errors[path] = 'Date can’t be before today.'
    } else if (previousDay !== undefined && day < previousDay) {
      errors[path] = 'Date must be on or after the previous Stop date.'
    }
    previousDay = Math.max(day, previousDay ?? day)
  })

  const amount = values.customer_rate_amount.trim()
  if (amount && toMinorUnits(amount) === null) {
    errors.customer_rate_amount = 'Enter a rate of zero or more, with up to two decimals.'
  }

  return errors
}

// A 24-hour window has no times worth showing.
const formatWindow = (time: TimePickerValue) =>
  time.type === '24' ? getTimeLabel('24') : formatDisplayTime(time)

/**
 * A folded card's summary: the first Stop's date, shown as a date, and the rest of the line, as in
 * "08:00 - 16:00 → ASAP | No reference | 500 GBP": the first Stop's window → the last Stop's, the
 * reference or "No reference", and the rate when there is one.
 */
export const getDuplicateCardSummary = ({
  customer_rate_amount,
  customer_rate_currency,
  drops,
}: DuplicateCardValues) => {
  const [first] = drops
  const last = drops.at(-1)
  const amount = toMinorUnits(customer_rate_amount)

  return {
    date: first?.date,
    details: [
      [first, last].map(stop => (stop ? formatWindow(stop.time) : '')).join(' → '),
      first?.reference_no?.trim() || 'No reference',
      amount === null
        ? null
        : formatCurrency({
            amount,
            currency: customer_rate_currency,
            showCurrencyCode: true,
            twoDecimals: true,
          }),
    ]
      .filter(Boolean)
      .join(' | '),
  }
}
